import { useCallback, useRef, useState } from 'react';
import { Menu, MenuItem, ListItemText, ListItemIcon, Divider, Box, Tooltip } from '@mui/material';
import { Visibility as ViewerIcon, ChevronRight } from '@mui/icons-material';
import type { Study } from '../../api/types';
import { MENU_GROUPS, STANDALONE_ACTIONS, type StudyActionDef, type MenuGroupDef } from './studyActions';
import { getAllViewers, getDefaultViewer } from '../viewer/viewerRegistry';
import { gwtStyles } from '../../config/theme';
import { pushError } from '../../stores/notificationStore';
import { useRefreshStudies } from './useStudies';

interface StudyContextMenuProps {
  mouseX: number;
  mouseY: number;
  studies: Study[];
  onClose: () => void;
  /** Open the study in a specific viewer */
  onOpenInViewer?: (viewerId: string) => void;
}

/**
 * GWT-matching right-click context menu — mirrors PopupMenu from StudyListPanel.java.
 *
 * CSS from blue theme:
 *   .brit-main-PopupMenu {
 *     border-radius: 10px;
 *     border: 2px solid darkblue;
 *     background-color: #FAF0E6;
 *   }
 *
 * Menu items: all StudyActions that are relevant to the clicked study.
 * Groups are separated by dividers with group labels.
 */
export const StudyContextMenu = ({ mouseX, mouseY, studies, onClose, onOpenInViewer }: StudyContextMenuProps) => {
  const refreshStudies = useRefreshStudies();
  const [executing, setExecuting] = useState(false);

  const viewers = getAllViewers();
  const defaultViewer = getDefaultViewer();
  const hasMultipleViewers = viewers.length > 1;
  const multiSelect = studies.length > 1;
  const primaryStudy = studies[0];

  const handleAction = useCallback(
    async (action: StudyActionDef) => {
      if (!action.backendReady || executing) return;
      setExecuting(true);
      onClose();
      try {
        await action.handler(studies);
        refreshStudies();
      } catch (e) {
        pushError(`Action "${action.label}" failed: ${e instanceof Error ? e.message : String(e)}`);
      } finally {
        setExecuting(false);
      }
    },
    [studies, onClose, refreshStudies, executing],
  );

  // Build menu items: standalone actions, then grouped actions with dividers
  const standaloneItems = STANDALONE_ACTIONS.filter(
    (a) => a.id !== 'REFRESH', // Refresh is on the toolbar already
  );

  return (
    <Menu
      open
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={{ top: mouseY, left: mouseX }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 200,
            maxWidth: 280,
            maxHeight: '70vh',
            borderRadius: '10px',
            border: `1px solid ${gwtStyles.borderSubtle}`,
            backgroundColor: gwtStyles.contextPopupBg,
          },
        },
      }}
    >
      {/* Study info header */}
      <Box sx={{ px: 2, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        {multiSelect ? (
          <Box sx={{ fontSize: 11, fontWeight: 600, color: 'text.primary' }}>
            {studies.length} studies selected
          </Box>
        ) : primaryStudy && (
          <>
            <Box sx={{ fontSize: 11, fontWeight: 600, color: 'text.primary' }}>
              {primaryStudy.name}
            </Box>
            <Box sx={{ fontSize: 10, color: 'text.secondary' }}>
              {primaryStudy.acc} — {primaryStudy.mod} {primaryStudy.date}
            </Box>
          </>
        )}
      </Box>

      {/* "Open in..." viewer entries */}
      {hasMultipleViewers && onOpenInViewer && (
        <>
          <Box
            sx={{ px: 2, py: 0.25, fontSize: 10, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}
          >
            Open in...
          </Box>
          {viewers.map((viewer) => (
            <MenuItem
              key={viewer.id}
              onClick={() => { onClose(); onOpenInViewer(viewer.id); }}
              sx={{ fontSize: 12, py: 0.25, minHeight: 26 }}
            >
              <ListItemIcon sx={{ minWidth: 24 }}>
                <ViewerIcon sx={{ fontSize: 14, color: viewer.id === defaultViewer.id ? gwtStyles.accentPrimary : 'text.secondary' }} />
              </ListItemIcon>
              <ListItemText
                primary={viewer.name}
                secondary={viewer.id === defaultViewer.id ? 'Default' : undefined}
                primaryTypographyProps={{ fontSize: 12 }}
                secondaryTypographyProps={{ fontSize: 9, color: gwtStyles.accentPrimary }}
              />
            </MenuItem>
          ))}
          <Divider sx={{ my: 0.25 }} />
        </>
      )}

      {/* Standalone actions */}
      {standaloneItems.map((action) => (
        <ActionMenuItem
          key={action.id}
          action={action}
          onClick={() => handleAction(action)}
        />
      ))}

      {/* Grouped actions — flyout submenus */}
      {MENU_GROUPS.map((group) => (
        <FlyoutSubMenu
          key={group.id}
          group={group}
          onAction={handleAction}
        />
      ))}
    </Menu>
  );
};

/* ── Flyout submenu for a group ── */

const FlyoutSubMenu = ({
  group,
  onAction,
}: {
  group: MenuGroupDef;
  onAction: (action: StudyActionDef) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const open = Boolean(anchorEl);

  const handleEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setAnchorEl(e.currentTarget);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setAnchorEl(null), 150);
  };

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  return (
    <>
      <Divider sx={{ my: 0.25 }} />
      <MenuItem
        onMouseEnter={handleEnter}
        onMouseLeave={scheduleClose}
        sx={{
          fontSize: 12,
          py: 0.25,
          minHeight: 26,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <ListItemText
          primary={group.label}
          primaryTypographyProps={{ fontSize: 12, fontWeight: 600 }}
        />
        <ChevronRight sx={{ fontSize: 16, ml: 1, color: 'text.secondary' }} />
      </MenuItem>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            onMouseEnter: cancelClose,
            onMouseLeave: scheduleClose,
            sx: {
              minWidth: 180,
              maxWidth: 260,
              borderRadius: '10px',
              border: `1px solid ${gwtStyles.borderSubtle}`,
              backgroundColor: gwtStyles.contextPopupBg,
            },
          },
        }}
        // Prevent the submenu from stealing focus and closing the parent
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        // Keep the parent menu open
        hideBackdrop
        style={{ pointerEvents: 'none' }}
      >
        <Box style={{ pointerEvents: 'auto' }}>
          {group.actions.map((action) => (
            <ActionMenuItem
              key={action.id}
              action={action}
              onClick={() => onAction(action)}
            />
          ))}
        </Box>
      </Menu>
    </>
  );
};

/* ── Menu item ── */

const ActionMenuItem = ({
  action,
  onClick,
}: {
  action: StudyActionDef;
  onClick: () => void;
}) => (
  <MenuItem
    onClick={onClick}
    disabled={!action.backendReady}
    sx={{
      fontSize: 12,
      py: 0.25,
      minHeight: 26,
      '&.Mui-disabled': { opacity: 0.5 },
    }}
  >
    <ListItemText
      primary={action.label}
      primaryTypographyProps={{ fontSize: 12 }}
    />
    {!action.backendReady && (
      <Tooltip title="Needs backend JSON-RPC method">
        <Box component="span" sx={{ ml: 1, fontSize: 10, color: 'text.disabled' }}>⚠</Box>
      </Tooltip>
    )}
  </MenuItem>
);
